import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  ReceiptLongOutlined as OrdersIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../components/PageHeader';
import PageTitleIcon from '../components/PageTitleIcon';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import PreOrderDetailDrawer, { preOrderMaterialLabel } from '../components/orders/PreOrderDetailDrawer';
import { useSelfSetupTour } from '../hooks/useSelfSetupTour';
import { useOrganizationStore } from '../stores/organization.store';
import { usePreOrderStore } from '../stores/pre-order.store';
import { useOrganizationWasteTypes } from '../hooks/useOrganizationWasteTypes';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { useTranslation } from 'react-i18next';
import {
  PRE_ORDER_WRITE_ROLES,
  PERM_PRE_ORDER_CREATE,
  PERM_PRE_ORDER_DELETE,
  PERM_PRE_ORDER_WRITE,
} from '../auth';
import {
  HELP_GUIDE_ORDERS,
  HELP_GUIDE_ORDERS_CREATE,
  HELP_GUIDE_ORDERS_SEARCH_FILTER,
  HELP_GUIDE_ORDERS_SUMMARY,
} from '../constants/knowledgeBaseTryIt';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import {
  PreOrderAmountUnit,
  PreOrderRequest,
  PreOrderSchema,
  PreOrderStatus,
  type PreOrderQueryParams,
} from '../types/pre-order.type';
import { OrganizationType } from '../types/organization.type';
import { getCatalogOptions } from '../services/waste-catalog.service';
import { getRecyclerPreOrdersOverview } from '../services/recycler-dashboard.service';
import type { CatalogMaterialOption, CatalogOptions } from '../types/waste-catalog.type';
import { formatLocalizedDate } from '../helpers/date-locale.helper';
import { getApiErrorDetail } from '../helpers/api-error.helper';

type OrderForm = {
  material_id: string;
  material_category: string;
  regulatory_code_id: string;
  physical_state: string;
  amount: string;
  amount_unit: PreOrderAmountUnit;
  status: PreOrderStatus;
  reference: string;
  notes: string;
};

type HelpOrdersPhase = 'summary' | 'filters' | 'list' | 'createButton' | 'create';

const EMPTY_FORM: OrderForm = {
  material_id: '',
  material_category: '',
  regulatory_code_id: '',
  physical_state: '',
  amount: '',
  amount_unit: PreOrderAmountUnit.KG,
  status: PreOrderStatus.PENDING,
  reference: '',
  notes: '',
};

const AMOUNT_UNIT_VALUES = [
  PreOrderAmountUnit.KG,
  PreOrderAmountUnit.TONS,
  PreOrderAmountUnit.LBS,
  PreOrderAmountUnit.BAGS,
  PreOrderAmountUnit.CARTONS,
  PreOrderAmountUnit.BOTTLES,
  PreOrderAmountUnit.PALLETS,
  PreOrderAmountUnit.CUBIC_YARDS,
  PreOrderAmountUnit.CUBIC_METERS,
  PreOrderAmountUnit.LITERS,
  PreOrderAmountUnit.GALLONS,
] as const;

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

function statusColor(status: string): 'default' | 'warning' | 'info' | 'success' | 'error' {
  if (status === PreOrderStatus.PENDING) return 'warning';
  if (status === PreOrderStatus.PARTIALLY_FULFILLED) return 'info';
  if (status === PreOrderStatus.COMPLETED) return 'success';
  if (status === PreOrderStatus.CANCELLED) return 'error';
  return 'default';
}

function formatFallbackLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id;
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

const Orders: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const selectedOrganization = useOrganizationStore((s) => s.selectedOrganization);
  const { canMutate, orgId } = useAuthPermissions();
  const canCreate = orgId ? canMutate(PERM_PRE_ORDER_CREATE(orgId), PRE_ORDER_WRITE_ROLES) : false;
  const canDelete = orgId ? canMutate(PERM_PRE_ORDER_DELETE(orgId), PRE_ORDER_WRITE_ROLES) : false;
  const canEdit = orgId ? canMutate(PERM_PRE_ORDER_WRITE(orgId), PRE_ORDER_WRITE_ROLES) : false;

  const tStatus = useCallback(
    (status: string) => t(`orderStatus.${status}`, { defaultValue: formatFallbackLabel(status) }),
    [t],
  );
  const tCategory = useCallback(
    (category: string) => t(`categories.${category}`, { defaultValue: formatFallbackLabel(category) }),
    [t],
  );
  const tUnitLabel = useCallback(
    (unit: string) => t(`units.labels.${unit}`, { defaultValue: formatFallbackLabel(unit) }),
    [t],
  );
  const num = useCallback(
    (value: number) => value.toLocaleString(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );
  const {
    isStep,
    tourPhase,
    advanceTourPhase,
    completeStep,
    dismissTour,
  } = useSelfSetupTour();

  const {
    preOrders,
    preOrdersLoading,
    preOrdersError,
    totalPreOrders,
    fetchPreOrders,
    createPreOrder,
    updatePreOrder,
    deletePreOrder,
    clearPreOrdersError,
  } = usePreOrderStore();

  const orgIsRecycler = selectedOrganization?.organization_type === OrganizationType.RECYCLER;
  const orgIdForQuery = selectedOrganization?.id;
  const { byMaterialId } = useOrganizationWasteTypes(orgIdForQuery, orgIsRecycler);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PreOrderSchema | null>(null);
  const [form, setForm] = useState<OrderForm>(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search.trim(), 400);
  const [statusFilter, setStatusFilter] = useState<'all' | PreOrderStatus>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [submitError, setSubmitError] = useState('');
  const [catalog, setCatalog] = useState<CatalogOptions | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [helpOrdersGuide, setHelpOrdersGuide] = useState(false);
  const [helpOrdersPhase, setHelpOrdersPhase] = useState<HelpOrdersPhase>('summary');
  /** Single-tip help guides end on first Next/Got it. */
  const [helpOrdersSingleTip, setHelpOrdersSingleTip] = useState(false);

  const showOrdersTour = isStep('orders') && tourPhase !== 'nav' && !helpOrdersGuide;
  const showSummaryTip =
    (showOrdersTour && tourPhase === 'summary') ||
    (helpOrdersGuide && helpOrdersPhase === 'summary');
  const showFiltersTip =
    (showOrdersTour && tourPhase === 'filters') ||
    (helpOrdersGuide && helpOrdersPhase === 'filters');
  const showListTip =
    (showOrdersTour && tourPhase === 'list') ||
    (helpOrdersGuide && helpOrdersPhase === 'list');
  const showCreateButtonTip =
    (showOrdersTour && tourPhase === 'createButton') ||
    (helpOrdersGuide && helpOrdersPhase === 'createButton');
  const showCreateTip =
    (showOrdersTour && tourPhase === 'create') ||
    (helpOrdersGuide && helpOrdersPhase === 'create');

  const finishHelpOrdersGuide = useCallback(() => {
    setDialogOpen(false);
    setHelpOrdersGuide(false);
    setHelpOrdersPhase('summary');
    setHelpOrdersSingleTip(false);
    clearHelpGuideActive();
  }, []);

  const softDismissOrdersTour = () => {
    if (helpOrdersGuide) {
      finishHelpOrdersGuide();
      return;
    }
    dismissTour();
  };

  useEffect(() => {
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (guide === HELP_GUIDE_ORDERS_CREATE) {
      markHelpGuideActive(guide);
      setHelpOrdersGuide(true);
      setHelpOrdersSingleTip(true);
      setHelpOrdersPhase('create');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    if (guide === HELP_GUIDE_ORDERS_SUMMARY) {
      markHelpGuideActive(guide);
      setHelpOrdersGuide(true);
      setHelpOrdersSingleTip(true);
      setHelpOrdersPhase('summary');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    if (guide === HELP_GUIDE_ORDERS_SEARCH_FILTER) {
      markHelpGuideActive(guide);
      setHelpOrdersGuide(true);
      setHelpOrdersSingleTip(true);
      setHelpOrdersPhase('filters');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }
    if (guide !== HELP_GUIDE_ORDERS) return;
    markHelpGuideActive(guide);
    setHelpOrdersGuide(true);
    setHelpOrdersSingleTip(false);
    setHelpOrdersPhase('summary');
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Filtering, search and paging are the backend's; the page shows one slice.
  const queryParams = useMemo<PreOrderQueryParams | null>(() => {
    if (!orgIdForQuery) return null;
    return {
      organization_id: orgIdForQuery,
      page: page + 1,
      limit: rowsPerPage,
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: debouncedSearch || undefined,
    };
  }, [orgIdForQuery, page, rowsPerPage, statusFilter, debouncedSearch]);

  useEffect(() => {
    if (!queryParams || !orgIsRecycler) return;
    void fetchPreOrders(queryParams);
  }, [fetchPreOrders, queryParams, orgIsRecycler]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter, debouncedSearch, rowsPerPage]);

  useEffect(() => {
    if (!orgIdForQuery || !orgIsRecycler) return;
    void getCatalogOptions('pre_order', orgIdForQuery).then(setCatalog).catch(() => setCatalog(null));
  }, [orgIdForQuery, orgIsRecycler]);

  const overviewQuery = useQuery({
    queryKey: ['recycler-pre-orders-overview', orgIdForQuery],
    queryFn: () => getRecyclerPreOrdersOverview(orgIdForQuery!),
    enabled: Boolean(orgIdForQuery && orgIsRecycler),
    retry: false,
  });

  const refreshAll = useCallback(() => {
    if (queryParams) void fetchPreOrders(queryParams);
    void queryClient.invalidateQueries({ queryKey: ['recycler-pre-orders-overview', orgIdForQuery] });
  }, [fetchPreOrders, queryParams, queryClient, orgIdForQuery]);

  const catalogMaterials = useMemo(
    () => (catalog?.categories ?? []).flatMap((category) => category.materials),
    [catalog],
  );
  const isArabic = i18n.language.startsWith('ar');
  const materialLabel = (material: CatalogMaterialOption) =>
    (isArabic ? material.name_ar : material.name_en) || material.name;

  const summary = useMemo(() => {
    const overview = overviewQuery.data;
    if (overview) {
      return {
        fromOverview: true,
        total: overview.total_orders,
        pending: overview.pending_count,
        partial: overview.partially_fulfilled_count,
        remainingKg: overview.remaining_kg,
      };
    }
    return {
      fromOverview: false,
      total: totalPreOrders || preOrders.length,
      pending: preOrders.filter((o) => o.status === PreOrderStatus.PENDING).length,
      partial: preOrders.filter((o) => o.status === PreOrderStatus.PARTIALLY_FULFILLED).length,
      remainingKg: preOrders.reduce((acc, o) => acc + Math.max(0, (o.amount_kg ?? 0) - (o.fulfilled_kg ?? 0)), 0),
    };
  }, [overviewQuery.data, preOrders, totalPreOrders]);

  const openCreate = useCallback(() => {
    // No silent default: a wrong-material order is worse than an empty field.
    setSubmitError('');
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }, []);

  const goToCreateDialogTip = () => {
    openCreate();
    if (helpOrdersGuide) {
      setHelpOrdersPhase('create');
      return;
    }
    advanceTourPhase();
  };

  const advanceOrdersTip = () => {
    if (helpOrdersGuide) {
      if (helpOrdersSingleTip) {
        finishHelpOrdersGuide();
        return;
      }
      if (helpOrdersPhase === 'summary') {
        setHelpOrdersPhase('filters');
        return;
      }
      if (helpOrdersPhase === 'filters') {
        setHelpOrdersPhase('list');
        return;
      }
      if (helpOrdersPhase === 'list') {
        setHelpOrdersPhase('createButton');
        return;
      }
      if (helpOrdersPhase === 'createButton') {
        goToCreateDialogTip();
        return;
      }
      finishHelpOrdersGuide();
      return;
    }
    if (tourPhase === 'create') {
      setDialogOpen(false);
      completeStep('orders');
      return;
    }
    if (tourPhase === 'createButton') {
      goToCreateDialogTip();
      return;
    }
    advanceTourPhase();
  };

  useEffect(() => {
    if (showCreateTip && !dialogOpen) {
      openCreate();
    }
  }, [showCreateTip, dialogOpen, openCreate]);

  const closeDialog = () => {
    setDialogOpen(false);
    setSubmitError('');
  };

  const openEdit = (order: PreOrderSchema) => {
    setSubmitError('');
    setEditingOrder(order);
    setForm({
      material_id: order.material_id ?? '',
      material_category: order.material_category,
      regulatory_code_id: order.regulatory_code_id ?? '',
      physical_state: order.physical_state_snapshot ?? '',
      amount: String(order.amount),
      amount_unit: order.amount_unit ?? PreOrderAmountUnit.KG,
      status: order.status,
      reference: String(order.data?.reference ?? ''),
      notes: String(order.data?.notes ?? ''),
    });
  };

  const closeEdit = () => {
    if (preOrdersLoading) return;
    setEditingOrder(null);
    setSubmitError('');
  };

  const saveEdit = async () => {
    if (!editingOrder) return;
    const saved = await updatePreOrder(editingOrder.id, {
      data: {
        ...(editingOrder.data ?? {}),
        reference: form.reference.trim(),
        notes: form.notes.trim(),
      },
    });
    if (saved) {
      closeEdit();
      refreshAll();
    } else {
      setSubmitError(usePreOrderStore.getState().preOrdersError ?? t('orders.updateError'));
    }
  };

  const createNextLabel = t('selfSetup.tourDone');
  const listNextLabel = t('selfSetup.tourNext');

  const submit = async () => {
    if (!orgIdForQuery) return;
    const parsedAmount = Number(form.amount);
    if (!form.material_id || !form.material_category || !form.regulatory_code_id || !form.amount || !(parsedAmount > 0)) {
      setSubmitError(t('orders.submitInvalid'));
      return;
    }

    const payload: PreOrderRequest = {
      material_category: form.material_category,
      material_id: form.material_id,
      // The alias every pickup is keyed by; the backend also resolves it, this
      // just keeps the request explicit.
      material_type_id: byMaterialId.get(form.material_id)?.material_type_id ?? undefined,
      regulatory_code_id: form.regulatory_code_id || undefined,
      physical_state: form.physical_state || undefined,
      organization_id: orgIdForQuery,
      amount: parsedAmount,
      amount_unit: form.amount_unit,
      data: {
        reference: form.reference.trim(),
        notes: form.notes.trim(),
      },
    };
    try {
      await createPreOrder(payload);
      const storeError = usePreOrderStore.getState().preOrdersError;
      if (storeError) {
        setSubmitError(storeError);
        return;
      }
      closeDialog();
      refreshAll();
    } catch (error) {
      setSubmitError(getApiErrorDetail(error) ?? t('orders.updateError'));
    }
  };

  const remove = async (order: PreOrderSchema) => {
    if (!window.confirm(t('orders.deleteConfirm'))) return;
    await deletePreOrder(order.id);
    refreshAll();
  };

  if (!selectedOrganization) {
    return <Alert severity="info">{t('orders.selectOrg')}</Alert>;
  }

  if (!orgIsRecycler) {
    return <Alert severity="warning">{t('orders.recyclerOnly')}</Alert>;
  }

  const selectedMaterial = catalogMaterials.find((row) => row.id === form.material_id) ?? null;
  const basketEmpty = catalog !== null && catalogMaterials.length === 0;

  return (
    <Box>
      <PageHeader
        icon={<OrdersIcon />}
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        action={
          <SetupCoachmark
            open={showCreateButtonTip}
            title={t('selfSetup.tour.ordersCreateButtonTitle')}
            subtitle={t('selfSetup.tour.ordersCreateButtonSubtitle')}
            onDismiss={softDismissOrdersTour}
            onNext={advanceOrdersTip}
            nextLabel={t('selfSetup.tourNext')}
          >
            <Button
              id="self-setup-orders-create"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (showCreateButtonTip) {
                  goToCreateDialogTip();
                  return;
                }
                openCreate();
              }}
              disabled={!canCreate}
            >
              {t('orders.create')}
            </Button>
          </SetupCoachmark>
        }
      />

      <SetupCoachmark
        open={showSummaryTip}
        title={t('selfSetup.tour.ordersSummaryTitle')}
        subtitle={t('selfSetup.tour.ordersSummarySubtitle')}
        onDismiss={softDismissOrdersTour}
        onNext={advanceOrdersTip}
        nextLabel={
          helpOrdersSingleTip && helpOrdersPhase === 'summary'
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
        fullWidth
      >
        <Grid
          container
          spacing={2}
          sx={{ mb: 2.5 }}
          id="self-setup-orders-summary"
        >
          {[
            [t('orders.totalOrders'), String(summary.total)],
            [t('orders.pending'), String(summary.pending)],
            [t('orders.partiallyFulfilled'), String(summary.partial)],
            [t('orders.remainingKg'), num(summary.remainingKg)],
          ].map(([label, value]) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Paper sx={{ p: 2, borderRadius: '8px' }} variant="outlined">
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
          {!summary.fromOverview && preOrders.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">{t('orders.overviewFallback')}</Typography>
            </Grid>
          )}
        </Grid>
      </SetupCoachmark>

      <SetupCoachmark
        open={showFiltersTip}
        title={t('selfSetup.tour.ordersFiltersTitle')}
        subtitle={t('selfSetup.tour.ordersFiltersSubtitle')}
        onDismiss={softDismissOrdersTour}
        onNext={advanceOrdersTip}
        nextLabel={
          helpOrdersSingleTip && helpOrdersPhase === 'filters'
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
        fullWidth
      >
        <Paper
          id="self-setup-orders-filters"
          sx={{ p: 2, mb: 2, borderRadius: '8px' }}
          variant="outlined"
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                size="small"
                label={t('orders.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                size="small"
                label={t('orders.status')}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | PreOrderStatus)}
              >
                <MenuItem value="all">{t('orders.allStatuses')}</MenuItem>
                {Object.values(PreOrderStatus).map((status) => (
                  <MenuItem key={status} value={status}>{tStatus(status)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </Paper>
      </SetupCoachmark>

      {preOrdersError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearPreOrdersError}>
          {preOrdersError}
        </Alert>
      )}

      <SetupCoachmark
        open={showListTip}
        title={t('selfSetup.tour.ordersListTitle')}
        subtitle={t('selfSetup.tour.ordersListSubtitle')}
        onDismiss={softDismissOrdersTour}
        onNext={advanceOrdersTip}
        nextLabel={listNextLabel}
        fullWidth
      >
      <TableContainer
        id="self-setup-orders-list"
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: '8px' }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('orders.order')}</TableCell>
              <TableCell>{t('orders.wasteType')}</TableCell>
              <TableCell align="right">{t('orders.amount')}</TableCell>
              <TableCell sx={{ minWidth: 170 }}>{t('orders.fulfilled')}</TableCell>
              <TableCell>{t('orders.status')}</TableCell>
              <TableCell>{t('orders.origin')}</TableCell>
              <TableCell>{t('orders.reference')}</TableCell>
              <TableCell>{t('orders.created')}</TableCell>
              <TableCell align="right">{t('orders.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {preOrdersLoading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : preOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      {statusFilter === 'all' && !debouncedSearch ? t('orders.emptyYet') : t('orders.emptyFiltered')}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              preOrders.map((order) => {
                const percent = Math.min(100, Math.max(0, Math.round(order.fulfillment_percent ?? 0)));
                return (
                  <TableRow
                    key={order.id}
                    hover
                    onClick={() => setDetailId(order.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{shortId(order.id)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{preOrderMaterialLabel(order, t)}</Typography>
                      {order.material_name_snapshot && (
                        <Typography variant="caption" color="text.secondary">{tCategory(order.material_category)}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {num(order.amount)} {order.amount_unit ? t(`units.${order.amount_unit}`, { defaultValue: order.amount_unit }) : ''}
                      {typeof order.amount_kg === 'number' && order.amount_unit !== PreOrderAmountUnit.KG && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          ≈ {num(order.amount_kg)} {t('units.kg')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LinearProgress
                          variant="determinate"
                          value={percent}
                          color={order.status === PreOrderStatus.COMPLETED ? 'success' : 'primary'}
                          sx={{ flex: 1, height: 6, borderRadius: 3 }}
                        />
                        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right', fontWeight: 600 }}>
                          {percent}%
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {num(order.fulfilled_kg ?? 0)} / {num(order.amount_kg ?? 0)} {t('units.kg')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={tStatus(order.status)} color={statusColor(order.status)} />
                    </TableCell>
                    <TableCell>
                      {order.origin === 'ops' ? (
                        <Tooltip title={t('orders.originOpsHint')}>
                          <Chip size="small" variant="outlined" color="secondary" label={t('orders.originOps')} />
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">{t('orders.originRecycler')}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{order.data?.reference || '—'}</TableCell>
                    <TableCell>{formatLocalizedDate(new Date(order.created_at), i18n.language)}</TableCell>
                    <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                      <Tooltip title={t('orders.view')}>
                        <IconButton size="small" onClick={() => setDetailId(order.id)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title={t('orders.editNotesReference')}>
                          <IconButton size="small" onClick={() => openEdit(order)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {order.status === PreOrderStatus.PENDING && canDelete && (
                        <Tooltip title={t('orders.delete')}>
                          <IconButton size="small" onClick={() => void remove(order)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalPreOrders}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          labelRowsPerPage={t('orders.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('orders.displayedRows', { from, to, count })}
        />
      </TableContainer>
      </SetupCoachmark>

      <PreOrderDetailDrawer
        open={Boolean(detailId)}
        preOrderId={detailId}
        organizationId={orgIdForQuery ?? ''}
        canWrite={canEdit}
        onClose={() => setDetailId(null)}
        onChanged={refreshAll}
      />

      <Dialog
        open={dialogOpen}
        onClose={showCreateTip ? undefined : closeDialog}
        fullWidth
        maxWidth="sm"
        hideBackdrop={showCreateTip}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
            // Above spotlight dim (modal + 5) so the dialog stays interactive & lit.
            zIndex: (t) => t.zIndex.modal + 6,
          },
        }}
      >
        <SetupCoachmark
          open={showCreateTip}
          title={t('selfSetup.tour.ordersCreateTitle')}
          subtitle={t('selfSetup.tour.ordersCreateSubtitle')}
          onDismiss={softDismissOrdersTour}
          onNext={advanceOrdersTip}
          nextLabel={createNextLabel}
          fullWidth
          flat
          placement="left"
          holeRadius={16}
        >
          <Box id="self-setup-orders-create-dialog">
            <DialogTitle
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 2,
                px: 3,
                pt: 2.5,
                pb: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <PageTitleIcon icon={<OrdersIcon />} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                    {t('orders.create')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                    {t('orders.createDialogSubtitle')}
                  </Typography>
                </Box>
              </Box>
              {!showCreateTip && (
                <IconButton
                  aria-label={t('common.close')}
                  onClick={closeDialog}
                  size="small"
                  sx={{
                    mt: -0.25,
                    color: 'text.secondary',
                    bgcolor: alpha(theme.palette.text.primary, 0.04),
                    '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.08) },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </DialogTitle>
            <Divider />
            <DialogContent
              sx={{
                px: 3,
                pt: 2.5,
                pb: 2.5,
                overflowX: 'hidden',
              }}
            >
              {basketEmpty ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{t('orders.noBasketTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('orders.noBasketBody')}</Typography>
                  <Button component={RouterLink} to="/materials" variant="contained" onClick={closeDialog}>
                    {t('orders.goToMaterials')}
                  </Button>
                </Box>
              ) : (
              <Stack spacing={2} sx={{ width: '100%', boxSizing: 'border-box' }}>
                {submitError && (
                  <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    {submitError}
                  </Alert>
                )}
                <TextField
                  select
                  fullWidth
                  label={t('orders.wasteType')}
                  value={form.material_id}
                  onChange={(e) => {
                    const material = catalogMaterials.find((row) => row.id === e.target.value);
                    if (!material) return;
                    setForm((previous) => ({
                      ...previous,
                      material_id: material.id,
                      material_category: material.category_code,
                      regulatory_code_id: (material.possible_regulatory_mappings.find((mapping) => mapping.is_default)
                        ?? material.possible_regulatory_mappings[0])?.regulatory_code_id ?? '',
                      physical_state: material.default_physical_state ?? '',
                    }));
                  }}
                  required
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                >
                  <MenuItem value="" disabled>
                    <em>{t('orders.selectWasteType')}</em>
                  </MenuItem>
                  {(catalog?.categories ?? []).flatMap((category) => [
                    <MenuItem key={`heading-${category.id}`} disabled value={`heading-${category.id}`}>
                      {(isArabic ? category.name_ar : category.name_en) || category.name}
                    </MenuItem>,
                    ...category.materials.map((material) => (
                      <MenuItem key={material.id} value={material.id} sx={{ pl: 4 }}>{materialLabel(material)}</MenuItem>
                    )),
                  ])}
                </TextField>
                {selectedMaterial && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label={t('orders.mwanSource')} value={selectedMaterial.mwan_source_category_name ?? t('orders.notSet')} disabled />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField fullWidth label={t('orders.physicalState')} value={form.physical_state ? formatFallbackLabel(form.physical_state) : t('orders.notSet')} disabled />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        select
                        fullWidth
                        required
                        label={t('orders.ewcCode')}
                        value={form.regulatory_code_id}
                        onChange={(event) => setForm((previous) => ({ ...previous, regulatory_code_id: event.target.value }))}
                        helperText={selectedMaterial.possible_regulatory_mappings.length > 1 ? t('orders.ewcHelpMany') : t('orders.ewcHelpOne')}
                      >
                        {selectedMaterial.possible_regulatory_mappings.map((mapping) => (
                          <MenuItem key={mapping.regulatory_code_id} value={mapping.regulatory_code_id}>
                            {mapping.code}{mapping.hazardous_marker === 'hazardous' ? ' *' : ''} — {isArabic ? mapping.description_ar || mapping.description_en : mapping.description_en}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                )}
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ width: '100%', alignItems: 'stretch' }}
                >
                  <TextField
                    fullWidth
                    label={t('orders.amount')}
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    required
                    inputProps={{ min: 0, step: 'any' }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                    }}
                  />
                  <TextField
                    select
                    fullWidth
                    label={t('orders.unit')}
                    value={form.amount_unit}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        amount_unit: e.target.value as PreOrderAmountUnit,
                      }))
                    }
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                    }}
                  >
                    {AMOUNT_UNIT_VALUES.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {tUnitLabel(unit)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  fullWidth
                  label={t('orders.reference')}
                  value={form.reference}
                  onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                  placeholder={t('orders.optional')}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
                <TextField
                  fullWidth
                  label={t('orders.notes')}
                  value={form.notes}
                  onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                  placeholder={t('orders.optional')}
                  multiline
                  minRows={3}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Stack>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
              <Button
                onClick={closeDialog}
                disabled={showCreateTip}
                sx={{ textTransform: 'none', borderRadius: '12px', px: 2 }}
              >
                {t('orders.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={() => void submit()}
                disabled={!canCreate || preOrdersLoading || basketEmpty}
                sx={{ textTransform: 'none', borderRadius: '12px', px: 2.5, fontWeight: 700 }}
              >
                {t('orders.create')}
              </Button>
            </DialogActions>
          </Box>
        </SetupCoachmark>
      </Dialog>

      <Dialog open={Boolean(editingOrder)} onClose={closeEdit} fullWidth maxWidth="md">
        <DialogTitle>{t('orders.editOrder')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            <Alert severity="info">{t('orders.editRestriction')}</Alert>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('orders.wasteType')}
                  value={editingOrder ? preOrderMaterialLabel(editingOrder, t) : ''}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('orders.status')}
                  value={tStatus(form.status)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label={t('orders.amount')} value={form.amount} disabled />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('orders.unit')}
                  value={tUnitLabel(form.amount_unit)}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('orders.reference')}
                  value={form.reference}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, reference: event.target.value }))
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={t('orders.notes')}
                  value={form.notes}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, notes: event.target.value }))
                  }
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={preOrdersLoading}>
            {t('orders.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void saveEdit()}
            disabled={!canEdit || preOrdersLoading}
          >
            {preOrdersLoading
              ? t('common.saving', { defaultValue: 'Saving…' })
              : t('common.saveChanges', { defaultValue: 'Save Changes' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Orders;
